// Firmark Calcs — shared calc-sheet state helpers.
//
// Extracted so the per-member workspace (NdsCalcWorkspace) and the project-level
// Add-Calculation catalog (AddCalculation) build a NEW sheet's create payload the
// SAME way — a sheet created in the catalog must round-trip losslessly when the
// workspace later reopens it. The single source of truth for: the supported role
// set, the calculator-grouped role menu, the per-role mark prefix, the role-from-
// name inference, the _ui_design lossless blob, and the next-mark numbering.

import type { CalcInputs, CalcSheet } from "../../types/calc";
import { loadTemplateIdFromCalculatorId } from "./loadCalcDefinitions";
import type { DesignInputs, EngineReq } from "./ndsTypes";
import { NDS_ROLES, defaultsForRole } from "./ndsRoles";
import { prepare } from "./ndsEngineProvider";

// The engine-backed roles (calculatorId !== null): the 5 wood-beam roles + the
// wood-column post + the wood-tension tie + the stud wall (wood_stud_wall_asd)
// + the biaxial beam-column (wood_biaxial_beam_column_asd). All 9 design roles
// are live.
export const SUPPORTED_ROLES = [
  "floor_joist",
  "rafter",
  "beam",
  "header",
  "ridge",
  "stud",
  "post",
  "beam_column",
  "tension",
] as const;
export type SupportedRole = (typeof SUPPORTED_ROLES)[number];

export function isSupportedRole(k: string): k is SupportedRole {
  return (SUPPORTED_ROLES as readonly string[]).includes(k);
}

// Role groups, by the calculator that backs each role (Beams = wood_beam_asd,
// Columns = wood_column_asd, Tension = wood_tension_asd).
export const ROLE_GROUPS: { label: string; roles: SupportedRole[] }[] = [
  { label: "Beams", roles: ["floor_joist", "rafter", "beam", "header", "ridge"] },
  { label: "Walls", roles: ["stud"] },
  { label: "Columns", roles: ["post", "beam_column"] },
  { label: "Tension", roles: ["tension"] },
];

export const MARK_PREFIX: Record<SupportedRole, string> = {
  floor_joist: "FJ",
  rafter: "R",
  beam: "B",
  header: "H",
  ridge: "RB",
  stud: "ST",
  post: "P",
  beam_column: "BC",
  tension: "T",
};

// Best-effort: infer a sheet's role from its name prefix. Only consulted for
// sheets created without the _ui_design round-trip blob. Order matters — longer
// prefixes first (RB before R, FJ before nothing) so a P-/T- axial sheet isn't
// shadowed.
export function roleFromName(name: string): SupportedRole {
  const up = name.toUpperCase();
  if (up.startsWith("FJ")) return "floor_joist";
  if (up.startsWith("RB")) return "ridge";
  if (up.startsWith("ST")) return "stud";
  if (up.startsWith("BC")) return "beam_column";
  if (up.startsWith("H")) return "header";
  if (up.startsWith("P")) return "post";
  if (up.startsWith("T")) return "tension";
  if (up.startsWith("R")) return "rafter";
  if (up.startsWith("B")) return "beam";
  return "floor_joist";
}

export const NEAR = 0.9;

// Calculators whose sheets are SCHEMA-DRIVEN (SchemaCalcSheet at /c/:sheetId),
// not member sheets: their inputs are the registry inputSchema's flat engine
// dict, never DesignInputs. The workspace redirects these on open. NOTE: the
// SCL / I-joist calcs are NOT here — those are member sheets reached by the
// material selector's dispatch (they carry _ui_design + a role).
const CONNECTION_SHEET_CALCS = [
  "wood_nail_connection_asd",
  "wood_bolt_connection_asd",
  "wood_lag_connection_asd",
  "wood_screw_connection_asd",
  "connector_product_capacity",
  "proprietary_hanger_connection",
  "proprietary_hold_down_connection",
];

const STEEL_SCHEMA_CALCS = [
  "steel_beam_aisc",
  "steel_column_aisc",
  "cfs_joist",
  "cfs_stud_wall",
  "cfs_header",
];

const CONCRETE_SCHEMA_CALCS = [
  "concrete_beam_aci",
  "continuous_footing",
  "pad_footing",
];

const MASONRY_SCHEMA_CALCS = [
  "masonry_bearing_wall_tms",
  "masonry_shear_wall_tms",
  "masonry_lintel_tms",
];

export const SCHEMA_SHEET_CALCS: ReadonlySet<string> = new Set([
  ...CONNECTION_SHEET_CALCS,
  ...STEEL_SCHEMA_CALCS,
  ...CONCRETE_SCHEMA_CALCS,
  ...MASONRY_SCHEMA_CALCS,
  "section_properties",
]);

export type CalcSheetGroup<T extends Pick<CalcSheet, "calculatorId"> = CalcSheet> = {
  id: string;
  label: string;
  members: T[];
};

const CALC_SHEET_GROUPS: {
  id: string;
  label: string;
  calculatorIds: readonly string[];
}[] = [
  {
    id: "beams",
    label: "Beams & joists",
    calculatorIds: ["wood_beam_asd", "wood_scl_beam_asd", "wood_ijoist_asd"],
  },
  { id: "walls", label: "Walls & studs", calculatorIds: ["wood_stud_wall_asd"] },
  {
    id: "columns",
    label: "Columns",
    calculatorIds: ["wood_column_asd", "wood_biaxial_beam_column_asd"],
  },
  { id: "tension", label: "Tension", calculatorIds: ["wood_tension_asd"] },
  {
    id: "connections",
    label: "Connections",
    calculatorIds: CONNECTION_SHEET_CALCS,
  },
  { id: "steel", label: "Steel", calculatorIds: STEEL_SCHEMA_CALCS },
  { id: "concrete", label: "Concrete", calculatorIds: CONCRETE_SCHEMA_CALCS },
  { id: "masonry", label: "Masonry", calculatorIds: MASONRY_SCHEMA_CALCS },
  { id: "analysis", label: "Analysis", calculatorIds: ["section_properties"] },
  {
    id: "wind",
    label: "Wind",
    calculatorIds: ["load_wind_mwfrs", "load_wind_cc"],
  },
  { id: "snow", label: "Snow", calculatorIds: ["load_snow"] },
  { id: "seismic", label: "Seismic", calculatorIds: ["load_seismic_elf"] },
  {
    id: "gravity",
    label: "Gravity",
    calculatorIds: ["load_dead_gravity", "load_wall_gravity"],
  },
  {
    id: "live-loads",
    label: "Live loads",
    calculatorIds: ["load_roof_live", "load_floor_deck_live", "load_ceiling_attic_live"],
  },
  { id: "rain-ice", label: "Rain & ice", calculatorIds: ["load_rain_ice"] },
];

/** Complete, stable navigation grouping. Unknown future calculators remain visible. */
export function groupCalcSheets<T extends Pick<CalcSheet, "calculatorId">>(sheets: T[]): CalcSheetGroup<T>[] {
  const assigned = new Set<T>();
  const groups = CALC_SHEET_GROUPS.map((group) => {
    const members = sheets.filter((sheet) => group.calculatorIds.includes(sheet.calculatorId));
    members.forEach((sheet) => assigned.add(sheet));
    return { id: group.id, label: group.label, members };
  }).filter((group) => group.members.length > 0);

  const other = sheets.filter((sheet) => !assigned.has(sheet));
  if (other.length > 0) groups.push({ id: "other", label: "Other calculations", members: other });
  return groups;
}

/** Route a sheet to the correct member or schema-driven workspace. */
export function calcSheetPath(
  projectId: string,
  sheet: Pick<CalcSheet, "id" | "calculatorId">,
  routeRoot = "/app/calculations",
): string {
  const surface = loadTemplateIdFromCalculatorId(sheet.calculatorId)
    ? "l"
    : SCHEMA_SHEET_CALCS.has(sheet.calculatorId)
      ? "c"
      : "m";
  return `${routeRoot}/${projectId}/${surface}/${sheet.id}`;
}

// Mark prefixes for schema-driven sheets. They are assigned independently
// Names do not inflate member-role mark counts because schema marks are
// generated by nextSchemaMark rather than the member role counter.
export const SCHEMA_MARK_PREFIX: Record<string, string> = {
  wood_nail_connection_asd: "CN",
  wood_bolt_connection_asd: "CB",
  wood_lag_connection_asd: "CL",
  wood_screw_connection_asd: "CS",
  connector_product_capacity: "PC",
  proprietary_hanger_connection: "PH",
  proprietary_hold_down_connection: "HD",
  steel_beam_aisc: "SB",
  steel_column_aisc: "SC",
  cfs_joist: "CJ",
  cfs_stud_wall: "CST",
  cfs_header: "CH",
  concrete_beam_aci: "RCB",
  continuous_footing: "CF",
  pad_footing: "PF",
  masonry_bearing_wall_tms: "MW",
  masonry_shear_wall_tms: "MSW",
  masonry_lintel_tms: "ML",
  section_properties: "SP",
};

export function buildReq(role: string, vals: DesignInputs): EngineReq {
  return { role, method: vals.designMethod ?? "ASD", near: NEAR, vals, disabledCombos: {} };
}

// The full design state (role + DesignInputs) is stashed in the sheet's engine
// inputs under a key the engine ignores, so a sheet round-trips LOSSLESSLY —
// every load row, condition and bracing edit survives a reopen. readUi restores
// it.
const UI_KEY = "_ui_design";

export function withUi(
  engineInputs: CalcInputs,
  role: SupportedRole,
  vals: DesignInputs,
): CalcInputs {
  return { ...engineInputs, [UI_KEY]: JSON.stringify({ role, vals }) };
}

export function readUi(
  stored: CalcInputs,
): { role: SupportedRole; vals: DesignInputs } | null {
  const raw = stored[UI_KEY];
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw) as { role?: string; vals?: DesignInputs };
    if (!parsed || typeof parsed !== "object" || !parsed.vals) return null;
    // Validate against ALL supported roles — a post/tension sheet must restore
    // as post/tension, not fall back to floor_joist.
    const role = isSupportedRole(parsed.role ?? "")
      ? (parsed.role as SupportedRole)
      : "floor_joist";
    return { role, vals: parsed.vals };
  } catch {
    return null;
  }
}

// Next mark number for a prefix, given the existing sheet names. Counts only
// sheets whose name actually STARTS with the prefix AND maps back to it (so
// "R-1" rafters don't inflate "RB-…" ridge numbering, and a schema sheet like
// "N-3" — whose roleFromName falls back to floor_joist — never inflates FJ).
export function nextMarkNumber(prefix: string, sheetNames: string[]): number {
  return sheetNames.filter(
    (n) =>
      n.toUpperCase().startsWith(prefix) &&
      MARK_PREFIX[roleFromName(n)] === prefix,
  ).length + 1;
}

// Next mark for a SCHEMA sheet prefix: plain startsWith count (schema names
// never collide with the member prefixes above by construction).
export function nextSchemaMark(prefix: string, sheetNames: string[]): string {
  const count = sheetNames.filter((n) => n.toUpperCase().startsWith(`${prefix}-`)).length;
  return `${prefix}-${count + 1}`;
}

// Build the create payload for a fresh sheet of `roleKey`, given the existing
// sheet names (for mark numbering). Returns the calculatorId, the chosen name,
// the engine inputs (with the lossless _ui_design blob), and the seeded design
// vals. Shared verbatim by the workspace's addMember and the catalog's pick.
// Returns null only for an unsupported role (calculatorId === null) — callers
// guard before calling.
export function buildSheetCreateInputs(
  roleKey: SupportedRole,
  existingSheetNames: string[],
  designMethod: "ASD" | "LRFD" = "ASD",
): { calculatorId: string; name: string; inputs: CalcInputs; vals: DesignInputs } | null {
  const calculatorId = NDS_ROLES[roleKey].calculatorId;
  if (!calculatorId) return null; // unreachable for SUPPORTED_ROLES; type guard
  const def = defaultsForRole(roleKey);
  def.designMethod = designMethod;
  const prefix = MARK_PREFIX[roleKey];
  const count = nextMarkNumber(prefix, existingSheetNames);
  const name = `${prefix}-${count}`;
  def.mark = name;
  const req = buildReq(roleKey, def);
  const p = prepare(req);
  // A fresh role default is always supported + complete, but guard anyway.
  const engineInputs =
    p.kind === "compute" ? p.inputs : { member_id: name, callout: def.size ?? "" };
  return { calculatorId, name, inputs: withUi(engineInputs, roleKey, def), vals: def };
}
